import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const MIN_AGE = 21;

function ageFrom(dobStr) {
  const dob = new Date(dobStr);
  if (isNaN(dob.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const m = today.getMonth() - dob.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age--;
  return age;
}

export default function AgeGate() {
  const [visible, setVisible] = useState(() => localStorage.getItem("isf_age_ok") !== "1");
  const [dob, setDob] = useState("");
  const [error, setError] = useState("");

  if (!visible) return null;

  const today = new Date().toISOString().split("T")[0];

  const verify = () => {
    if (!dob) { setError("Please enter your date of birth."); return; }
    const age = ageFrom(dob);
    if (age === null) { setError("That date doesn't look right."); return; }
    if (age < MIN_AGE) {
      setError(`You must be ${MIN_AGE} or older to enter. Sorry!`);
      return;
    }
    localStorage.setItem("isf_age_ok", "1");
    localStorage.setItem("isf_dob", dob);
    setVisible(false);
  };

  return (
    <AnimatePresence>
      <motion.div
        data-testid="age-gate"
        className="fixed inset-0 z-[200] flex items-center justify-center bg-black px-6"
        initial={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <div
          className="absolute inset-0 opacity-30 bg-cover bg-center"
          style={{ backgroundImage: "url(https://images.unsplash.com/photo-1698614083129-15e976a503fe?crop=entropy&cs=srgb&fm=jpg&q=60&w=1600)" }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/80 to-black/40" />
        <motion.div
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="relative z-10 max-w-lg text-center"
        >
          <img src="/logo-mark.png" alt="ISEEFANTASY" className="h-20 w-20 mx-auto mb-4 object-contain mix-blend-screen" />
          <p className="font-accent text-xs uppercase tracking-[0.4em] text-primary mb-6">Adults Only · 21+</p>
          <h1 className="font-display text-5xl md:text-6xl leading-none mb-6">
            ISEE<span className="italic text-primary">FANTASY</span>
          </h1>
          <p className="text-muted-foreground text-base md:text-lg mb-8 leading-relaxed">
            This site contains explicit adult material intended for mature audiences. Enter your
            date of birth to confirm you are <span className="text-foreground font-medium">{MIN_AGE} years or older</span>.
          </p>

          <div className="max-w-xs mx-auto mb-4 text-left">
            <label className="block text-xs uppercase tracking-widest text-muted-foreground mb-2">Date of birth</label>
            <input
              data-testid="age-gate-dob"
              type="date"
              max={today}
              value={dob}
              onChange={(e) => { setDob(e.target.value); setError(""); }}
              className="w-full bg-card/80 border border-border px-4 py-3.5 text-foreground focus:border-primary outline-none transition-colors [color-scheme:dark]"
            />
            {error && (
              <p data-testid="age-gate-error" className="mt-3 text-sm text-primary bg-primary/10 border border-primary/30 px-3 py-2">
                {error}
              </p>
            )}
          </div>

          <div className="flex flex-col sm:flex-row gap-3 justify-center max-w-xs mx-auto sm:max-w-none">
            <button
              data-testid="age-gate-enter"
              onClick={verify}
              className="px-10 py-4 bg-primary text-primary-foreground font-accent font-bold uppercase tracking-widest text-sm transition-transform active:scale-95 hover:bg-[#e6004c]"
            >
              Confirm & Enter
            </button>
            <a
              href="https://www.google.com"
              data-testid="age-gate-exit"
              className="px-10 py-4 border border-border text-muted-foreground font-accent uppercase tracking-widest text-sm hover:text-foreground hover:border-foreground transition-colors flex items-center justify-center"
            >
              Leave
            </a>
          </div>

          <p className="mt-6 text-xs text-muted-foreground/70 max-w-xs mx-auto">
            By entering you agree you are of legal age and consent to viewing adult content.
          </p>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
